<template>
    <div
        class="audit"
    >
        <list
            :data="{
                list,
                pagination,
                hasLoading,
                headers: [
                    {
                        type: 'component',
                        value: 'contacts',
                        component: () => import( /* webpackChunkName: 'BaseContacts' */ '@/components/bundles/contacts/BaseContacts' )
                    },
                    { type: 'default', value: 'taxpayerIdentificationNumber' },
                    { type: 'default', value: 'campaign' },
                    { type: 'date', value: 'receptionDate', originFormat: 'DD-MM-YYYY HH:mm', finalFormat: 'DD-MM-YYYY HH:mm' },
                    { type: 'default', translate: 'state', value: 'latestContactCallState' }
                ],
                destak: 'name',
                auditState: {
                    type: 'component',
                    value: 'id',
                    component: () => import( /* webpackChunkName: 'BaseStates' */ '@/components/bundles/states/BaseAuditStates' )
                },
            }"
        />
        <widget-options>
            <widget-button
                v-if="$store.getters.can( 'audit', 'seeTotals' )"
                class="capitalize"
                :settings="{
                    action: () => toggleModal({ modalName: 'seeTotals' }),
                    label: 'seeTotals'
                }"
            />
            <widget-anchor
                v-if="$store.getters.can( 'audit', 'addContact' )"
                :settings="{
                    url: `/audit/add`,
                    label: 'addContact'
                }"
            />
        </widget-options>
        <modal
            v-show="modals.seeTotals.show"
            :form="{
                class: 'modal--see-totals'
            }"
            :customSettings="{
                color: 'green',
                class: 'modal--see-totals__content',
                show: {
                    infoSection: false,
                    actions: false
                }
            }"
            @close="toggleModal({ modalName: 'seeTotals', show: false })"
            :key="modals.seeTotals.show" >
            <template #modal__header >
                <h2 class="modal--see-totals__header">{{ $t('global.seeTotals') }}</h2>
            </template>
            <template #modal__body >
                <base-field
                    class="script__campaigns"
                    :settings="{
                        label: { class: 'capitalize', text: $t('global.campaign') },
                        input: {
                            component: 'InputAutocomplete',
                            name: 'campaign',
                            id: 'campaign',
                            type: 'text',
                            value: '',
                            targetClass: 'scrip__campaigns__input field__input--default typography--default color--default',
                            browserAutocomplete: 'off',
                            isMissing: errors.missing.campaign,
                            dynamicData: {
                                data: campaigns.drop,
                                ordering: campaigns.loading
                            },
                            noListener: true
                        },
                        allowFilling: true
                    }"
                />
                <div v-if="payload.campaign" class="modal--see-totals__charts display--flex">
                    <base-card class="modal--see-totals__charts__totals-by-typification">
                        <template #card__title >
                            {{ $t('reporting.totalsByTypification') }}
                        </template>
                        <template #card__body >
                            <div class="campaign__body">
                                <base-chart
                                    :loading="modals.seeTotals.charts.totalsByTypification.loading"
                                    :chart="modals.seeTotals.charts.totalsByTypification.data"
                                />
                            </div>
                        </template>
                    </base-card>
                    <base-card class="modal--see-totals__charts__final-typifications-by-hour">
                        <template #card__title >
                            {{ $t('reporting.finalTypificationsByHour') }}
                        </template>
                        <template #card__body >
                            <div class="campaign__body">
                                <base-chart
                                    :loading="modals.seeTotals.charts.finalTypificationsByHour.loading"
                                    :chart="modals.seeTotals.charts.finalTypificationsByHour.data"
                                />
                            </div>
                        </template>
                    </base-card>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
// hooks
import { listStructure } from '@/hooks/use-data';

// packages
import { reactive, watch, toRefs } from '@vue/composition-api';

// store
import { storePayload, useStore } from '@/hooks/use-store';

// helpers
import { sortObjectsArray } from '@/helpers/mixinGeneral';

// requests
import { get } from '@/helpers/requests';

// mixins
import {
    addNewBarChart,
    addNewPieChart,
    finalTypificationsByHour,
    totalsByTypification
} from '@/helpers/mixinCharts';

export default {
    components: {
        ...listStructure.components,
        WidgetButton: () => import( /* webpackChunkName: 'WidgetButton' */ '@/components/bundles/layout/widget/options/types/WidgetButton' ),
        WidgetAnchor: () => import( /* webpackChunkName: 'WidgetAnchor' */ '@/components/bundles/layout/widget/options/types/WidgetAnchor' ),
        Modal: () => import( /* webpackChunkName: 'Modal' */ '@/components/bundles/layout/modals/Modal' ),
        BaseField: () => import( /* webpackChunkName: 'BaseField'  */ '@/components/bundles/fields/BaseField' ),
        BaseCard: () => import( /* webpackChunkName: 'BaseCard' */ '@/components/bundles/cards/BaseCard' ),
        BaseChart: () => import( /* webpackChunkName: 'BaseChart' */ '@/components/bundles/charts/BaseChart' )
    },
    props: {
        ...listStructure.props
    },
    setup () {
        const { actions } = useStore();
        const { errors, payload } = storePayload();

        const modals = reactive({
            seeTotals: {
                show: false,
                charts: {
                    totalsByTypification: { data: null, loading: false },
                    finalTypificationsByHour: { data: null, loading: false }
                }
            }
        });

        // Variáveis que guardam info de pedidos feitos à API.
        const requestData = reactive({
            campaigns: {
                drop: [],
                loading: false
            },
            reports: null
        });

        // Função que faz abrir ou fechar um modal.
        const toggleModal = ({ modalName, show = true }) => {
            modals[modalName].show = show;
            actions( 'controlActionBar', { value: show });
        };

        // Ao abrir o modal faz o pedido das campanhas se ainda não tiver sido feito.
        watch( () => modals.seeTotals.show,
            showModalSeeTotals => {
                if ( showModalSeeTotals && !requestData.campaigns.drop.length )
                    getCampaignsRequest();
            });

        const getCampaignsRequest = ( async () => {
            requestData.campaigns.loading = true;
            requestData.campaigns.drop = await get( '/campaigns' )
                .then( ({ data }) => data.content.data );
            requestData.campaigns.drop = requestData.campaigns.drop.map( key => ({ value: key.name, label: key.name }) );
            // ordenar drop dos das campanhas
            sortObjectsArray( requestData.campaigns.drop );
            requestData.campaigns.loading = false;
        });

        // Ao mudar a campanha no modal, faz pedido à API dos dados dos
        // gráficos dessa campanha, se ainda não tiver sido feito o pedido.
        watch( () => payload.value.campaign,
            async newCampaign => {
                if ( newCampaign ) {
                    const chartNames = Object.keys( modals.seeTotals.charts );

                    // Inicialização dos loading de todos os charts da página.
                    chartNames.forEach( chart => { modals.seeTotals.charts[chart].loading = true; });

                    requestData.reports = await get( `/reporting?campaignName=${newCampaign}` )
                        .then( ({ data }) => data.content.data[0] );
                } else
                    requestData.reports = null;
            });

        // Ao receber dados de reporting de uma nova campanha, cria
        // os dados necessários para mostrar os gráficos ao utilizador.
        watch( () => requestData.reports,
            newReportingData => {
                if ( newReportingData ) {
                    // Adiciona gráfico que mostra os totais por tificação.
                    modals.seeTotals.charts.totalsByTypification.data = addNewPieChart({
                        statisticType: totalsByTypification,
                        updatedData: newReportingData.totalTipifications,
                        chartOptions: {
                            legendPosition: 'bottom',
                            legendHeight: 75,
                            legendHorizontalAlign: 'left'
                        }
                    });
                    modals.seeTotals.charts.totalsByTypification.loading = false;

                    // Adiciona gráfico que mostra as tipificações finais por hora.
                    modals.seeTotals.charts.finalTypificationsByHour.data = addNewBarChart({
                        statisticType: finalTypificationsByHour,
                        updatedData: newReportingData.contactCallsPerHour,
                        chartOptions: {
                            chartHeight: 'auto',
                            showYAxis: false,
                            showXAxisLabels: true,
                            barBorderRadius: 5
                        }
                    });
                    modals.seeTotals.charts.finalTypificationsByHour.loading = false;
                }
            });

        return {
            modals,
            ...toRefs( requestData ),
            toggleModal,
            errors,
            payload
        };
    }
};
</script>

<style lang="scss">
.list__body__group__latestContactCallState{
    grid-area: 2 / 2 / 3 / 4;
}

.modal {
    &--see-totals {
        &--transition {
            &-enter-active, &-leave-active {
                height: 412px;
                opacity: 1;
                overflow: hidden;
                @include transition( height .3s ease-in-out, opacity .2s ease-in-out );
            }
            &-enter, &-leave-to {
                height: 0;
                opacity: 0;
                @include transition( height .3s ease-in-out .2s, opacity .3s ease-in-out );
                will-change: height, opacity;
            }
        }
        &__header {
            @include typography-specs( 25px, 33px, 700 );
        }
        &__content {
            max-width: 1400px;
        }
        &__body {
            margin-top: 20px;
        }
        &__charts {
            margin-top: 30px;
            gap: 20px;
        }
    }
}
</style>